import {
  intranetBookmarkStateSchema,
  intranetReactionStateSchema,
  intranetViewStateSchema,
  type IntranetContentListResponse,
  type IntranetFeedItem,
  type IntranetReactionKind,
} from '@gb-ticket/shared-types';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiFetch } from '@/lib/api';
import { intranetContentsKey } from '@/products/intranet/features/intranet/use-intranet';

/** Like toggle with an optimistic count. A failed request restores the previous cache. */
export function useToggleIntranetLike(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (liked: boolean) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/reactions`,
        intranetReactionStateSchema,
        { method: liked ? 'POST' : 'DELETE' },
      ),
    onMutate: async (liked) => {
      await queryClient.cancelQueries({ queryKey: intranetContentsKey });
      const snapshots = queryClient.getQueriesData({ queryKey: intranetContentsKey });
      patchFeedItem(queryClient, slug, (item) => withLike(item, liked));
      return { snapshots };
    },
    onError: (_error, _liked, context) => {
      restore(queryClient, context?.snapshots);
      toast.error('„Gefällt mir“ konnte nicht gespeichert werden.');
    },
    onSuccess: (state) => {
      patchFeedItem(queryClient, slug, (item) => ({
        ...item,
        likedByMe: state.liked,
        likeCount: state.likeCount,
      }));
    },
  });
}

/** Sets the one reaction on a news post. Sending the current kind again removes it. */
export function useSetIntranetReaction(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reaction: IntranetReactionKind) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/reactions`,
        intranetReactionStateSchema,
        { method: 'POST', body: JSON.stringify({ reaction }) },
      ),
    onSuccess: (state) => {
      patchFeedItem(queryClient, slug, (item) => ({
        ...item,
        likedByMe: state.liked,
        likeCount: state.likeCount,
        myReaction: state.myReaction ?? null,
        reactions: state.counts ?? item.reactions,
      }));
    },
    onError: () => {
      toast.error('Die Reaktion konnte nicht gespeichert werden.');
    },
  });
}

/**
 * Bookmark toggle. Saved-list queries gain or lose the entry so the channel count
 * and „Gespeicherte Beiträge“ stay in step. A failed request restores the cache.
 */
export function useToggleIntranetBookmark(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookmarked }: { bookmarked: boolean; item: IntranetFeedItem }) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/bookmark`,
        intranetBookmarkStateSchema,
        { method: bookmarked ? 'POST' : 'DELETE' },
      ),
    onMutate: async ({ bookmarked, item }) => {
      await queryClient.cancelQueries({ queryKey: intranetContentsKey });
      const snapshots = queryClient.getQueriesData({ queryKey: intranetContentsKey });
      rewriteBookmarks(queryClient, slug, bookmarked, item);
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      restore(queryClient, context?.snapshots);
      toast.error('Der Beitrag konnte nicht gespeichert werden.');
    },
    onSuccess: (state, { item }) => {
      rewriteBookmarks(queryClient, slug, state.bookmarked, state.bookmarked ? item : undefined);
    },
  });
}

/** One view per person and Berlin day. The server count replaces the cached number. */
export function useRecordIntranetView(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/intranet/contents/${encodeURIComponent(slug)}/view`, intranetViewStateSchema, {
        method: 'POST',
      }),
    onSuccess: (state) => {
      patchFeedItem(queryClient, slug, (item) => ({ ...item, viewCount: state.viewCount }));
    },
  });
}

function withLike(item: IntranetFeedItem, liked: boolean): IntranetFeedItem {
  if (item.likedByMe === liked) {
    return item;
  }
  return {
    ...item,
    likedByMe: liked,
    likeCount: Math.max(0, item.likeCount + (liked ? 1 : -1)),
  };
}

type ListFilter = {
  saved?: boolean;
  pinned?: boolean;
  page?: number;
  pageSize?: number;
};

function rewriteBookmarks(
  queryClient: QueryClient,
  slug: string,
  bookmarked: boolean,
  source: IntranetFeedItem | undefined,
): void {
  for (const [key, data] of queryClient.getQueriesData({ queryKey: intranetContentsKey })) {
    queryClient.setQueryData(key, nextBookmark(data, key, slug, bookmarked, source));
  }
}

function nextBookmark(
  data: unknown,
  key: readonly unknown[],
  slug: string,
  bookmarked: boolean,
  source: IntranetFeedItem | undefined,
): unknown {
  if (isFeedItem(data) && data.slug === slug) {
    return { ...data, bookmarkedByMe: bookmarked };
  }
  if (isInfinite(data)) {
    return {
      ...data,
      pages: data.pages.map((page) => mapListBookmark(page, key, slug, bookmarked, source)),
    };
  }
  if (!isList(data)) {
    return data;
  }
  return mapListBookmark(data, key, slug, bookmarked, source);
}

function mapListBookmark(
  data: IntranetContentListResponse,
  key: readonly unknown[],
  slug: string,
  bookmarked: boolean,
  source: IntranetFeedItem | undefined,
): IntranetContentListResponse {
  const flagged = data.items.map((item) =>
    item.slug === slug ? { ...item, bookmarkedByMe: bookmarked } : item,
  );
  const filter = readListFilter(key);
  if (!filter?.saved) {
    return { ...data, items: flagged };
  }
  const present = data.items.some((item) => item.slug === slug);
  if (!bookmarked && present) {
    return {
      ...data,
      total: Math.max(0, data.total - 1),
      items: flagged.filter((item) => item.slug !== slug),
    };
  }
  if (
    bookmarked &&
    !present &&
    source &&
    (filter.page ?? 1) === 1 &&
    fitsSavedList(filter, source)
  ) {
    const pageSize = filter.pageSize ?? flagged.length + 1;
    return {
      ...data,
      total: data.total + 1,
      items: [{ ...source, bookmarkedByMe: true }, ...flagged].slice(0, pageSize),
    };
  }
  return { ...data, items: flagged };
}

/** A saved+pinned query only receives pins; a saved stream query only receives the rest. */
function fitsSavedList(filter: ListFilter, item: IntranetFeedItem): boolean {
  if (filter.pinned === true) {
    return item.pinned;
  }
  if (filter.pinned === false) {
    return !item.pinned;
  }
  return true;
}

function readListFilter(key: readonly unknown[]): ListFilter | null {
  const index = key.indexOf('list');
  const filter = key[index + 1];
  if (!isRecord(filter)) {
    return null;
  }
  return {
    saved: filter.saved === true,
    pinned: typeof filter.pinned === 'boolean' ? filter.pinned : undefined,
    page: typeof filter.page === 'number' ? filter.page : undefined,
    pageSize: typeof filter.pageSize === 'number' ? filter.pageSize : undefined,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function patchFeedItem(
  queryClient: QueryClient,
  slug: string,
  patch: (item: IntranetFeedItem) => IntranetFeedItem,
): void {
  queryClient.setQueriesData({ queryKey: intranetContentsKey }, (data: unknown) => {
    if (isInfinite(data)) {
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => (item.slug === slug ? patch(item) : item)),
        })),
      };
    }
    if (isList(data)) {
      return {
        ...data,
        items: data.items.map((item) => (item.slug === slug ? patch(item) : item)),
      };
    }
    if (isFeedItem(data) && data.slug === slug) {
      return patch(data);
    }
    return data;
  });
}

function restore(
  queryClient: QueryClient,
  snapshots: [readonly unknown[], unknown][] | undefined,
): void {
  for (const [key, data] of snapshots ?? []) {
    queryClient.setQueryData(key, data);
  }
}

function isList(data: unknown): data is IntranetContentListResponse {
  return Boolean(
    data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items),
  );
}

/** Home feed stream pages from `useIntranetFeedStream`. */
function isInfinite(
  data: unknown,
): data is { pages: IntranetContentListResponse[]; pageParams: unknown[] } {
  return Boolean(
    data &&
    typeof data === 'object' &&
    Array.isArray((data as { pages?: unknown }).pages) &&
    Array.isArray((data as { pageParams?: unknown }).pageParams) &&
    (data as { pages: unknown[] }).pages.every(isList),
  );
}

function isFeedItem(data: unknown): data is IntranetFeedItem {
  return Boolean(
    data &&
    typeof data === 'object' &&
    typeof (data as { slug?: unknown }).slug === 'string' &&
    typeof (data as { likeCount?: unknown }).likeCount === 'number' &&
    !('items' in data) &&
    !('pages' in data),
  );
}
